// Alliance helpers shared by the tabs and the no-alliance view.
import { type AllianceRank, RANK_ORDER } from '@stars-and-ships/api-types';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { queryKeys } from '../../api/queries';
import { Button } from '../ui';

export const RANK_LABELS: Record<AllianceRank, string> = {
  overlord: 'Overlord',
  warlord: 'Warlord',
  commander: 'Commander',
  recruit: 'Recruit',
};

export const atLeast = (rank: AllianceRank, required: AllianceRank) =>
  RANK_ORDER[rank] >= RANK_ORDER[required];

/** Everything the alliance changes: the player's own state, what they see, and alliance lists. */
export function useRefresh(galaxyId: string) {
  const queryClient = useQueryClient();
  return () => {
    for (const key of [
      queryKeys.alliances(galaxyId),
      queryKeys.galaxyMe(galaxyId),
      queryKeys.map(galaxyId),
      queryKeys.systems(galaxyId),
      queryKeys.fleets(galaxyId),
      queryKeys.intel(galaxyId),
      queryKeys.battles(galaxyId),
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };
}

/** A destructive action asks once before it runs. */
export function ConfirmButton({
  label,
  question,
  confirmLabel,
  disabled,
  onConfirm,
  variant = 'secondary',
}: {
  label: string;
  question: string;
  confirmLabel: string;
  disabled?: boolean;
  onConfirm: () => void;
  variant?: 'secondary' | 'ghost';
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <Button variant={variant} disabled={disabled} onClick={() => setAsking(true)}>
        {label}
      </Button>
    );
  }
  return (
    <div className="w-full space-y-2 hud-cut-sm border border-accent/50 bg-accent/10 p-3 text-sm text-accent">
      <p>{question}</p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => {
            setAsking(false);
            onConfirm();
          }}
        >
          {confirmLabel}
        </Button>
        <Button variant="ghost" onClick={() => setAsking(false)}>
          Keep
        </Button>
      </div>
    </div>
  );
}

/** Ranks the caller may give a member (04 §5.14): Warlords up to Commander on lower ranks. */
export function assignableRanks(myRank: AllianceRank, target: AllianceRank): AllianceRank[] {
  if (target === 'overlord') return [];
  if (myRank === 'overlord') return ['warlord', 'commander', 'recruit'];
  if (myRank === 'warlord' && RANK_ORDER[target] < RANK_ORDER.warlord) {
    return ['commander', 'recruit'];
  }
  return [];
}

/** G-64: when the alliance may be renamed again, or null when it may be renamed now. */
export function renameBlockedUntil(
  alliance: { renameAllowedAt: string | null },
  now: number
): Date | null {
  if (!alliance.renameAllowedAt) return null;
  const allowedAt = new Date(alliance.renameAllowedAt);
  return allowedAt.getTime() > now ? allowedAt : null;
}
